import { Behaviour } from "../utilities/ResponsiveBehaviour.js";

/**
 * Mobile navigation controller:
 * - Toggles the whole nav via an external button [aria-controls="<nav id>"].
 * - Manages nested submenus (<ol role="menu" level="N">) with one open per level.
 * - Syncs aria-expanded on all controls with [aria-controls].
 * - Click-outside closes the whole nav.
 * - "Toggle Submenus" button collapses level 3 if any are open, otherwise level 2.
 * - On open, reveals the level that contains the aria-current item (deepest wins).
 * - Focuses the first visible, enabled <input> inside the menu after opening.
 */
export class MenuMobile implements Behaviour {
    // ---------- Root & initial state ----------
    private rootEl: HTMLElement;
    private initialRootHidden: boolean = true;

    private initialModeAttr: string = "desktop";

    // ---------- Top-level menu ----------
    private topLevelMenuEl: HTMLElement | null = null;
    private initialTopLevelOrientation: string = "horizontal";

    // ---------- Sublevel container (wrapper for deeper levels) ----------
    private sublevelContainerEl: HTMLElement | null = null;
    private initialSublevelContainerHidden: boolean = true;
    private initialSublevelContainerRole: string = "dialog";

    // ---------- Action buttons present at construction time (and their initial hidden state) ----------
    private actionButtons: [HTMLElement, boolean][] = [];

    // ---------- External nav toggle button (outside <nav>) ----------
    private navToggleButton: HTMLButtonElement | null | undefined = null;

    // ---------- Optional content dialog inside the nav ----------
    private contentDialogEl: HTMLDivElement | null = null;
    private initialContentDialogHidden: boolean = true;

    // ---------- "Toggle Submenus" control inside the sublevel container ----------
    private submenusToggleButton: HTMLButtonElement | null = null;

    // ============================================================
    // Event handlers (bound once for add/remove symmetry)
    // ============================================================

    /**
     * Delegate clicks inside the nav to any control having [aria-controls],
     * and toggle the corresponding <ol role="menu">.
     */
    private onControlsClick = (e: Event) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;

        const control = target.closest<HTMLElement>('[aria-controls]');
        if (!control || !this.rootEl.contains(control)) return;

        const id = control.getAttribute('aria-controls');
        if (!id) return;

        const controlled = document.getElementById(id) as HTMLElement | null;
        if (!controlled || !this.rootEl.contains(controlled)) return;

        if (controlled.getAttribute('role') !== 'menu') return;

        controlled.hidden ? this.openMenu(controlled) : this.closeMenu(controlled);
    };

    /**
     * Close the whole nav when clicking outside, unless the click was on the
     * external toggle button controlling this nav.
     */
    private onDocumentPointerDown = (e: PointerEvent) => {
        const t = e.target as Node | null;
        if (!t) return;

        const clickInsideNav = this.rootEl.contains(t);
        const clickOnExternalToggler =
            !!(this.navToggleButton && (this.navToggleButton === t || this.navToggleButton.contains(t)));

        if (!clickInsideNav && !clickOnExternalToggler && this.navToggleButton?.getAttribute('aria-expanded') === 'true') {
            this.hideMenu();
        }
    };

    /**
     * "Toggle Submenus" behavior:
     * - If any level 3 menus are open, close all level 3.
     * - Else if any level 2 menus are open, close all level 2.
     */
    private onSubmenusToggleClick = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.isAnyMenuOpenAtLevel(3)) {
            this.closeMenusAtLevel(3);
            return;
        }
        if (this.isAnyMenuOpenAtLevel(2)) {
            this.closeMenusAtLevel(2);
        }
    };

    // ============================================================
    // Lifecycle
    // ============================================================

    constructor(root: HTMLElement) {
        this.rootEl = root;

        // Capture initial state
        this.initialRootHidden = this.rootEl.hidden;
        this.initialModeAttr = this.rootEl.parentElement?.getAttribute("mode") ?? this.initialModeAttr;

        // Key structure references
        this.topLevelMenuEl = this.rootEl.querySelector<HTMLElement>(":scope > div:first-of-type > ol:first-of-type");
        this.initialTopLevelOrientation =
            this.topLevelMenuEl?.getAttribute("aria-orientation") ?? this.initialTopLevelOrientation;

        this.navToggleButton = this.rootEl.parentElement?.querySelector<HTMLButtonElement>(`[aria-controls="${root.id}"]`);

        this.sublevelContainerEl = this.rootEl.querySelector<HTMLElement>(":scope > div:first-of-type > div:first-of-type");
        this.initialSublevelContainerHidden = this.sublevelContainerEl?.hidden ?? true;
        this.initialSublevelContainerRole = this.sublevelContainerEl?.getAttribute("role") ?? this.initialSublevelContainerRole;

        // Collect current action buttons and their initial hidden state
        const foundButtons = this.rootEl.querySelectorAll<HTMLElement>([
            ":scope > div:first-of-type > ol > li > .btn:only-child",
            ":scope > div:first-of-type > ol > li > button:only-child"
        ].join(", "));
        foundButtons.forEach(btn => {
            this.actionButtons.push([btn.parentElement ?? btn, btn.hidden]);
        });

        // Optional dialog inside the nav
        this.contentDialogEl = this.rootEl.querySelector<HTMLDivElement>(`:scope > [role="dialog"]:last-of-type`);
        this.initialContentDialogHidden = this.contentDialogEl?.hidden ?? true;

        // "Toggle Submenus" button — structural selection (no aria-label)
        this.submenusToggleButton =
            this.sublevelContainerEl?.querySelector<HTMLButtonElement>(':scope > button:first-of-type') ?? null;
    }

    /** Apply mobile behavior (called on activate) */
    public apply() {
        // Switch to mobile mode and hide root by default
        this.rootEl.hidden = true;
        this.rootEl.parentElement?.setAttribute("mode", "mobile");

        // Make top level vertical in mobile
        this.topLevelMenuEl?.setAttribute("aria-orientation", "vertical");

        // Sublevel container becomes presentational and visible in mobile
        if (this.sublevelContainerEl) {
            this.sublevelContainerEl.hidden = false;
            this.sublevelContainerEl.setAttribute("role", "presentation");
        }

        // Wire external toggle
        if (this.navToggleButton) {
            this.navToggleButton.addEventListener("click", this.toggleMenu);
        }

        // Ensure optional dialog is visible in mobile
        if (this.contentDialogEl) {
            this.contentDialogEl.hidden = false;
        }

        // Hide action buttons (moved into mobile UI)
        this.actionButtons.forEach(([btnWrapper]) => {
            btnWrapper.hidden = true;
        });

        // Delegated submenu toggling and outside-click handling
        this.rootEl.addEventListener('click', this.onControlsClick);
        document.addEventListener('pointerdown', this.onDocumentPointerDown, true);

        // Initial ARIA sync
        this.syncAriaExpandedAll();

        // Wire "Toggle Submenus" if present
        if (this.submenusToggleButton) {
            this.submenusToggleButton.addEventListener('click', this.onSubmenusToggleClick);
        }
    }

    /** Revert to initial desktop behavior (called on deactivate) */
    public destroy() {
        // Restore initial visibility and mode
        this.rootEl.hidden = this.initialRootHidden;
        this.rootEl.parentElement?.setAttribute("mode", this.initialModeAttr);

        // Restore top-level orientation
        this.topLevelMenuEl?.setAttribute("aria-orientation", this.initialTopLevelOrientation);

        // Restore sublevel container
        if (this.sublevelContainerEl) {
            this.sublevelContainerEl.hidden = this.initialSublevelContainerHidden;
            this.sublevelContainerEl.setAttribute("role", this.initialSublevelContainerRole);
        }

        // Unwire external toggle
        if (this.navToggleButton) {
            this.navToggleButton.removeEventListener("click", this.toggleMenu);
            this.navToggleButton.setAttribute("aria-expanded", "false");
        }

        // Restore optional dialog visibility
        if (this.contentDialogEl) {
            this.contentDialogEl.hidden = this.initialContentDialogHidden;
        }

        // Restore action buttons' initial hidden state
        this.actionButtons.forEach(([btnWrapper, initiallyHidden]) => {
            btnWrapper.hidden = initiallyHidden;
        });

        // Remove listeners
        this.rootEl.removeEventListener('click', this.onControlsClick);
        document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);

        if (this.submenusToggleButton) {
            this.submenusToggleButton.removeEventListener('click', this.onSubmenusToggleClick);
        }
    }

    // ============================================================
    // Root menu show/hide
    // ============================================================

    /** Open the root mobile menu */
    private showMenu() {
        this.navToggleButton?.setAttribute("aria-expanded", "true");
        this.rootEl.hidden = false;

        // Reveal the level containing the current item (or collapse to just level 1)
        this.revealCurrentOnOpen();
    }

    /** Close the root mobile menu */
    private hideMenu() {
        this.navToggleButton?.setAttribute("aria-expanded", "false");
        this.rootEl.hidden = true;

        // Hygiene: collapse all submenus at level >= 2
        this.closeMenusAtOrBelowLevel(2);
    }

    /** External toggle handler */
    private toggleMenu = () => {
        if (this.navToggleButton?.getAttribute("aria-expanded") === "false") {
            this.showMenu();
        } else {
            this.hideMenu();
        }
    };

    // ============================================================
    // Menu helpers (levels, opening/closing, ARIA)
    // ============================================================

    /** Read numeric level from a <ol role="menu" level="N"> element (defaults to 0) */
    private getMenuLevel(el: Element | null): number {
        if (!el) return 0;
        const raw = (el as HTMLElement).getAttribute('level') ?? '0';
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
    }

    /** Set aria-expanded on all controls pointing to a given id */
    private setControlsExpandedForId(id: string, expanded: boolean) {
        this.rootEl.querySelectorAll<HTMLElement>(`[aria-controls="${id}"]`)
            .forEach(ctrl => ctrl.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
    }

    /** Sync aria-expanded of every control based on target visibility */
    private syncAriaExpandedAll() {
        this.rootEl.querySelectorAll<HTMLElement>('[aria-controls]').forEach(ctrl => {
            const id = ctrl.getAttribute('aria-controls');
            if (!id) return;
            const target = document.getElementById(id) as HTMLElement | null;
            if (!target || !this.rootEl.contains(target)) return;
            if (target.getAttribute('role') !== 'menu') return;
            ctrl.setAttribute('aria-expanded', target.hidden ? 'false' : 'true');
        });
    }

    /** Open a submenu (one open per level; closes deeper levels) */
    private openMenu(menuEl: HTMLElement) {
        const level = this.getMenuLevel(menuEl);
        if (level <= 1) return; // level 1 is always visible; unmanaged here

        menuEl.hidden = false;
        if (menuEl.id) this.setControlsExpandedForId(menuEl.id, true);

        // Close siblings at the same level
        this.rootEl.querySelectorAll<HTMLElement>(`[role="menu"][level="${level}"]`).forEach(other => {
            if (other === menuEl) return;
            other.hidden = true;
            if (other.id) this.setControlsExpandedForId(other.id, false);
        });

        // Close deeper levels to avoid orphaned panels
        this.closeMenusBelowLevel(level);
    }

    /** Close a submenu and anything deeper than it */
    private closeMenu(menuEl: HTMLElement) {
        const level = this.getMenuLevel(menuEl);
        if (level <= 1) return;

        menuEl.hidden = true;
        if (menuEl.id) this.setControlsExpandedForId(menuEl.id, false);

        // Also close levels below
        this.closeMenusBelowLevel(level);
    }

    /** Close all menus deeper than the given level */
    private closeMenusBelowLevel(level: number) {
        this.rootEl.querySelectorAll<HTMLElement>('[role="menu"][level]').forEach(m => {
            const l = this.getMenuLevel(m);
            if (l > level) {
                m.hidden = true;
                if (m.id) this.setControlsExpandedForId(m.id, false);
            }
        });
    }

    /** Close all menus at or below the given level */
    private closeMenusAtOrBelowLevel(level: number) {
        this.rootEl.querySelectorAll<HTMLElement>('[role="menu"][level]').forEach(m => {
            const l = this.getMenuLevel(m);
            if (l >= level) {
                m.hidden = true;
                if (m.id) this.setControlsExpandedForId(m.id, false);
            }
        });
    }

    /** Is any menu open at a specific level? */
    private isAnyMenuOpenAtLevel(level: number): boolean {
        return Array.from(this.rootEl.querySelectorAll<HTMLElement>(`[role="menu"][level="${level}"]`))
            .some(m => !m.hidden);
    }

    /** Close all menus at a specific level */
    private closeMenusAtLevel(level: number) {
        this.rootEl.querySelectorAll<HTMLElement>(`[role="menu"][level="${level}"]`).forEach(m => {
            m.hidden = true;
            if (m.id) this.setControlsExpandedForId(m.id, false);
        });
    }

    // ============================================================
    // Reveal current item on open
    // ============================================================

    /**
     * When opening the nav:
     * - If an aria-current item exists, open the chain of menus down to its level.
     * - Otherwise, collapse everything at level >= 2 (only level 1 remains).
     */
    private revealCurrentOnOpen() {
        // Prefer the deepest "current" marker (supports multiple/breadcrumb-like cases)
        const candidates = Array.from(
            this.rootEl.querySelectorAll<HTMLElement>('[aria-current="true"], [aria-current="page"]')
        );

        let targetMenu: HTMLElement | null = null;
        let targetLevel = 1;

        for (const el of candidates) {
            const menu = el.closest<HTMLElement>('[role="menu"][level]');
            const level = this.getMenuLevel(menu);
            if (menu && level >= targetLevel) {
                targetMenu = menu;
                targetLevel = level;
            }
        }

        if (!targetMenu) {
            // No current item – collapse to just level 1
            this.closeMenusAtOrBelowLevel(2);
            this.syncAriaExpandedAll();
            return;
        }

        // Build ancestor chain from top to target (open in order)
        const chain: HTMLElement[] = [];
        let m: HTMLElement | null = targetMenu;
        while (m) {
            chain.unshift(m);
            m = this.findParentMenuEl(m);
        }

        for (const menu of chain) {
            if (this.getMenuLevel(menu) > 1) this.openMenu(menu);
        }

        // Ensure ARIA is correct after forced state
        this.syncAriaExpandedAll();
    }

    /**
     * Find the parent <ol role="menu"> of a child menu by:
     * 1) Locating a control with aria-controls=<child.id>,
     * 2) Walking up to the nearest ancestor <ol role="menu"> that contains that control.
     */
    private findParentMenuEl(childMenuEl: HTMLElement): HTMLElement | null {
        const id = childMenuEl.id;
        if (!id) return null;

        const control = this.rootEl.querySelector<HTMLElement>(`[aria-controls="${id}"]`);
        if (!control) return null;

        return control.closest<HTMLElement>('[role="menu"][level]') ?? null;
    }
}
